import type { JSX } from "react";
import Image from "next/image";
import Link from "next/link";
import { getAssetUrl } from "@/lib/directus";
import type { Article } from "@/lib/directus";
import { trackArticleClick } from "@/lib/analytics";

interface ArticleListViewProps {
  articles: Article[];
  clickSource?: string;
}

// Category badge color mapping
const CATEGORY_COLORS: Record<string, string> = {
  Training: "bg-blue-600 group-hover:bg-blue-700",
  Nutrition: "bg-green-600 group-hover:bg-green-700",
  Lifestyle: "bg-purple-600 group-hover:bg-purple-700",
};

export function ArticleListView({
  articles,
  clickSource = "blog",
}: ArticleListViewProps): JSX.Element {
  return (
    <div className="mt-12 space-y-12 sm:mt-16 sm:space-y-16 md:mt-20 md:space-y-20">
      {articles.map((article) => {
        const imageData =
          typeof article.featured_image === "object" && article.featured_image
            ? article.featured_image
            : null;
        const imageUrl = article.featured_image
          ? getAssetUrl(article.featured_image)
          : "";
        // Use publish_date for display (date_updated is only for SEO metadata)
        const publishDate = article.publish_date || article.date_created;

        // Extract plain text excerpt from HTML content
        const excerpt = article.content.replace(/<[^>]*>/g, "").substring(0, 200);

        // Get badge color based on article category
        const badgeColor = CATEGORY_COLORS[article.category] || "bg-gray-600 group-hover:bg-gray-700";

        return (
          <article
            key={article.id}
            className="group relative isolate flex flex-col gap-6 rounded-2xl border border-transparent p-4 transition-all duration-200 hover:-translate-y-1 hover:border-gray-200 hover:shadow-md active:translate-y-0 active:shadow-sm sm:gap-8 sm:p-5 lg:flex-row"
          >
            {imageUrl && (
              <div className="relative aspect-[16/9] overflow-hidden rounded-2xl sm:aspect-[2/1] lg:aspect-square lg:w-64 lg:shrink-0">
                <Image
                  src={imageUrl}
                  alt={imageData?.description || article.title}
                  fill
                  className="bg-gray-50 object-cover"
                  sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 256px"
                />
                <div className="absolute inset-0 rounded-2xl ring-1 ring-inset ring-gray-900/10" />
              </div>
            )}
            <div>
              <div className="flex items-center gap-x-4 text-xs sm:text-sm">
                <time dateTime={publishDate} className="text-gray-500" suppressHydrationWarning>
                  {new Date(publishDate).toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </time>
                <span
                  className={`relative z-10 rounded-full ${badgeColor} px-3 py-1.5 font-medium text-white transition-colors duration-200`}
                >
                  {article.category}
                </span>
              </div>
              <div className="relative">
                <h2 className="mt-3 text-lg font-semibold leading-6 text-gray-900 transition-colors duration-200 group-hover:text-indigo-600 sm:text-xl md:text-2xl">
                  <Link
                    href={`/blog/${article.slug}`}
                    className="focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-600 focus-visible:ring-offset-2"
                    onClick={() =>
                      trackArticleClick(
                        article.slug,
                        article.category ?? "",
                        clickSource
                      )
                    }
                  >
                    <span className="absolute inset-0" />
                    {article.title}
                  </Link>
                </h2>
                <p className="mt-4 text-sm leading-6 text-gray-600 sm:mt-5 sm:text-base md:leading-7">
                  {excerpt}...
                </p>
              </div>
            </div>
          </article>
        );
      })}
    </div>
  );
}
